import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { InitialEnquiryModalValues } from '../types';
import type { ISearchPageAds } from '../api/types/searchPageApiTypes';
import { DealerBusinessTypes } from '../api/types/adviewApiTypes';
import { ENVIRONMENT_NAME } from '../utils';

interface AdDetailsProviderProps {
  children: ReactNode;
  userSaved?: boolean;
  section?: string;
  dealerId?: number;
}

type category = {
  id: number;
  description: string;
};

interface AdDetailsContextI {
  isReportAdModalOpen: boolean;
  setIsReportAdModalOpen: (t: boolean) => void;
  adId: number;
  setAdId: (t: number) => void;
  categories?: category[];
  setCategories: (t: category[]) => void;
  currentReportAdView: 'INITIAL' | 'SUCCESS';
  setCurrentReportAdView: (t: 'INITIAL' | 'SUCCESS') => void;
  isSendMessageModalOpen: boolean;
  setIsSendMessageModalOpen: (t: boolean) => void;
  isShareModalOpen: boolean;
  setIsShareModalOpen: (t: boolean) => void;
  isAdSaved: boolean;
  setIsAdSaved: (t: boolean) => void;
  isDealerEnquiryModalOpen: boolean;
  setIsDealerEnquiryModalOpen: (t: boolean) => void;
  enquiry?: InitialEnquiryModalValues;
  setEnquiry: (t: InitialEnquiryModalValues) => void;
  isYoutubeReviewOpen: boolean;
  setIsYoutubeReviewOpen: (t: boolean) => void;
  similarAds: ISearchPageAds[];
  setSimilarAds: (recommendedAds: ISearchPageAds[]) => void;
  isPostEnquirySimilarAdsModalOpen: boolean;
  setIsPostEnquirySimilarAdsModalOpen: (t: boolean) => void;
  isPricePerMonthModalOpen: boolean;
  setIsPricePerMonthModalOpen: (t: boolean) => void;
  isTrustedDealerModalOpen: boolean;
  setTrustedDealerModalOpen: (t: boolean) => void;
  isContactSellerModalOpen: boolean;
  setIsContactSellerModalOpen: (isOpen: boolean) => void;
  isReservationModalOpen: boolean;
  setIsReservationModalOpen: (isOpen: boolean) => void;
  isDealerBusinessTypeModalOpen: boolean;
  dealerBusinessTypeModalName: DealerBusinessTypes | null;
  setDealerBusinessTypeModalName: (name: DealerBusinessTypes | null) => void;
  enableLoggedOutDealerEnquiry?: boolean;
}

const AdDetailsContext = createContext<AdDetailsContextI>({
  isReportAdModalOpen: false,
  setIsReportAdModalOpen: () => null,
  adId: 0,
  setAdId: () => null,
  categories: [],
  setCategories: () => null,
  currentReportAdView: 'INITIAL',
  setCurrentReportAdView: () => null,
  isSendMessageModalOpen: false,
  setIsSendMessageModalOpen: () => null,
  isShareModalOpen: false,
  setIsShareModalOpen: () => null,
  isAdSaved: false,
  setIsAdSaved: () => null,
  isDealerEnquiryModalOpen: false,
  setIsDealerEnquiryModalOpen: () => null,
  enquiry: undefined,
  setEnquiry: () => null,
  isYoutubeReviewOpen: false,
  setIsYoutubeReviewOpen: () => null,
  similarAds: [],
  setSimilarAds: () => null,
  isPostEnquirySimilarAdsModalOpen: false,
  setIsPostEnquirySimilarAdsModalOpen: () => null,
  isPricePerMonthModalOpen: false,
  setIsPricePerMonthModalOpen: () => null,
  isTrustedDealerModalOpen: false,
  setTrustedDealerModalOpen: () => null,
  isContactSellerModalOpen: false,
  setIsContactSellerModalOpen: () => null,
  isReservationModalOpen: false,
  setIsReservationModalOpen: () => null,
  isDealerBusinessTypeModalOpen: false,
  dealerBusinessTypeModalName: null,
  setDealerBusinessTypeModalName: () => null,
  enableLoggedOutDealerEnquiry: false,
});

const AdDetailsProvider = (props: AdDetailsProviderProps) => {
  const { children, userSaved, dealerId } = props;
  const [isReportAdModalOpen, setIsReportAdModalOpen] =
    useState<boolean>(false);
  const [isTrustedDealerModalOpen, setTrustedDealerModalOpen] =
    useState<boolean>(false);
  const [adId, setAdId] = useState<number>(0);
  const [categories, setCategories] = useState<category[]>();
  const [currentReportAdView, setCurrentReportAdView] = useState<
    'INITIAL' | 'SUCCESS'
  >('INITIAL');

  const [isSendMessageModalOpen, setIsSendMessageModalOpen] =
    useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isAdSaved, setIsAdSaved] = useState<boolean>(userSaved ?? false);
  const [isDealerEnquiryModalOpen, setIsDealerEnquiryModalOpen] =
    useState<boolean>(false);
  const [enquiry, setEnquiry] = useState<InitialEnquiryModalValues | undefined>(
    undefined,
  );
  const [
    isPostEnquirySimilarAdsModalOpen,
    setIsPostEnquirySimilarAdsModalOpen,
  ] = useState<boolean>(false);
  const [isYoutubeReviewOpen, setIsYoutubeReviewOpen] =
    useState<boolean>(false);
  const [similarAds, setSimilarAds] = useState<ISearchPageAds[]>([]);
  const [isPricePerMonthModalOpen, setIsPricePerMonthModalOpen] =
    useState<boolean>(false);
  const [isContactSellerModalOpen, setIsContactSellerModalOpen] =
    useState(false);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [dealerBusinessTypeModalName, setDealerBusinessTypeModalName] =
    useState<DealerBusinessTypes | null>(null);
  const isDealerBusinessTypeModalOpen = Boolean(dealerBusinessTypeModalName);

  const TEST_DEALER_ID = 2413;

  const enableLoggedOutDealerEnquiry =
    props.section === 'cars' &&
    (ENVIRONMENT_NAME !== 'production' || dealerId === TEST_DEALER_ID);

  const memoizedContextValue = useMemo(
    () => ({
      isReportAdModalOpen,
      setIsReportAdModalOpen,
      adId,
      setAdId,
      categories,
      setCategories,
      currentReportAdView,
      setCurrentReportAdView,
      isSendMessageModalOpen,
      setIsSendMessageModalOpen,
      isShareModalOpen,
      setIsShareModalOpen,
      isAdSaved,
      setIsAdSaved,
      isDealerEnquiryModalOpen,
      setIsDealerEnquiryModalOpen,
      enquiry,
      setEnquiry,
      isYoutubeReviewOpen,
      setIsYoutubeReviewOpen,
      similarAds,
      setSimilarAds,
      isPostEnquirySimilarAdsModalOpen,
      setIsPostEnquirySimilarAdsModalOpen,
      isPricePerMonthModalOpen,
      setIsPricePerMonthModalOpen,
      isTrustedDealerModalOpen,
      setTrustedDealerModalOpen,
      isContactSellerModalOpen,
      setIsContactSellerModalOpen,
      isReservationModalOpen,
      setIsReservationModalOpen,
      isDealerBusinessTypeModalOpen,
      dealerBusinessTypeModalName,
      setDealerBusinessTypeModalName,
      enableLoggedOutDealerEnquiry,
    }),
    [
      isReportAdModalOpen,
      setIsReportAdModalOpen,
      adId,
      setAdId,
      categories,
      setCategories,
      currentReportAdView,
      setCurrentReportAdView,
      isSendMessageModalOpen,
      setIsSendMessageModalOpen,
      isShareModalOpen,
      setIsShareModalOpen,
      isAdSaved,
      setIsAdSaved,
      isDealerEnquiryModalOpen,
      setIsDealerEnquiryModalOpen,
      enquiry,
      setEnquiry,
      isYoutubeReviewOpen,
      setIsYoutubeReviewOpen,
      similarAds,
      setSimilarAds,
      isPostEnquirySimilarAdsModalOpen,
      setIsPostEnquirySimilarAdsModalOpen,
      isPricePerMonthModalOpen,
      setIsPricePerMonthModalOpen,
      isTrustedDealerModalOpen,
      setTrustedDealerModalOpen,
      isContactSellerModalOpen,
      setIsContactSellerModalOpen,
      isReservationModalOpen,
      setIsReservationModalOpen,
      isDealerBusinessTypeModalOpen,
      dealerBusinessTypeModalName,
      setDealerBusinessTypeModalName,
      enableLoggedOutDealerEnquiry,
    ],
  );

  return (
    <AdDetailsContext.Provider value={memoizedContextValue}>
      {children}
    </AdDetailsContext.Provider>
  );
};
const useAdDetailsContext = () => useContext(AdDetailsContext);

export { AdDetailsProvider, useAdDetailsContext };
